/**
 * WebSocketService for real-time device updates
 * Integrates with the WebSocket API for device state changes
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { getCurrentUser } from './AuthService';
import { Hub } from 'aws-amplify/utils';

export interface WebSocketMessage {
  event: string;
  deviceId: string;
  state: Record<string, any>;
  timestamp: string;
}

class WebSocketService {
  private socket: WebSocket | null = null;
  private messageHandlers: ((message: WebSocketMessage) => void)[] = [];
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private connectionId: string | null = null;
  private authToken: string | null = null;
  private authTokenExpiry: number = 0;
  
  // WebSocket API endpoint from environment variables
  private webSocketUrl = process.env.REACT_APP_WEBSOCKET_API_URL || 'wss://fpjcriluf1.execute-api.us-east-1.amazonaws.com/dev';
  
  constructor() {
    // Listen for authentication events
    this.setupAuthListener();
  }
  
  /**
   * Set up listener for authentication events
   */
  private setupAuthListener(): void {
    Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      
      if (event === 'signedIn') {
        console.log('User signed in, refreshing WebSocket connection');
        this.authToken = null;
        this.reconnect();
      } else if (event === 'signedOut') {
        console.log('User signed out, disconnecting WebSocket');
        this.authToken = null;
        this.disconnect();
      } else if (event === 'tokenRefresh') {
        console.log('Auth token refreshed, updating WebSocket connection');
        this.authToken = null;
        this.reconnect();
      }
    });
  }
  
  /**
   * Force a reconnection of the WebSocket
   */
  private reconnect(): void {
    if (this.isConnected && this.socket) {
      this.socket.close();
      this.isConnected = false;
    }
    
    // Attempt to reconnect immediately
    this.connect().catch(error => {
      console.error('Error reconnecting WebSocket:', error);
    });
  }
  
  /**
   * Connect to the WebSocket API
   * @returns Promise that resolves when connected
   */
  /**
   * Get authentication token for WebSocket connection
   * @returns Promise resolving to the auth token
   */
  private async getAuthToken(): Promise<string | null> {
    try {
      // Check if we have a valid cached token
      const now = Date.now();
      if (this.authToken && this.authTokenExpiry > now) {
        console.log('Using cached auth token');
        return this.authToken;
      }
      
      // Get a fresh token
      console.log('Fetching fresh auth token');
      
      // Try to get the current user first
      try {
        const user = await getCurrentUser();
        console.log('Current user for WebSocket auth:', user);
      } catch (userError) {
        console.warn('Error getting current user for WebSocket:', userError);
      }
      
      try {
        // Get credentials instead of just the session
        const session = await fetchAuthSession();
        console.log('Auth session details:', {
          hasTokens: !!session.tokens,
          hasIdToken: !!(session.tokens && session.tokens.idToken),
          hasAccessToken: !!(session.tokens && session.tokens.accessToken),
          hasCredentials: !!session.credentials
        });
        
        // Try to use the access token instead of ID token
        if (session && session.tokens && session.tokens.accessToken) {
          const token = session.tokens.accessToken.toString();
          console.log('Using access token for WebSocket authentication');
          
          // Cache the token with expiry (5 minutes before actual expiry)
          this.authToken = token;
          
          // Set expiry to 55 minutes (tokens typically last 1 hour)
          this.authTokenExpiry = now + (55 * 60 * 1000);
          
          return token;
        }
        
        // Fall back to ID token if access token is not available
        if (session && session.tokens && session.tokens.idToken) {
          const token = session.tokens.idToken.toString();
          console.log('Using ID token for WebSocket authentication');
          
          // Cache the token with expiry (5 minutes before actual expiry)
          this.authToken = token;
          
          // Set expiry to 55 minutes (tokens typically last 1 hour)
          this.authTokenExpiry = now + (55 * 60 * 1000);
          
          return token;
        }
      } catch (authError) {
        console.warn('Could not get auth token from session, continuing without authentication:', authError);
      }
      
      // If we can't get a token, return empty string to connect without auth
      // This allows anonymous connections for public devices
      return '';
    } catch (error) {
      console.error('Error getting auth token:', error);
      // Return empty string instead of null to allow connection without auth
      return '';
    }
  }

  async connect(): Promise<void> {
    if (this.isConnected && this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected, reusing connection');
      return Promise.resolve();
    }
    
    return new Promise((resolve, reject) => {
      // Set a timeout to prevent hanging indefinitely
      const connectionTimeout = setTimeout(() => {
        console.warn('WebSocket connection timeout after 10 seconds');
        // Resolve with a warning instead of rejecting to prevent cascading failures
        resolve();
      }, 10000); // 10 seconds timeout
      
      try {
        // Close any existing socket before creating a new one
        if (this.socket) {
          try {
            this.socket.close();
          } catch (closeError) {
            console.warn('Error closing existing WebSocket:', closeError);
          }
          this.socket = null;
        }
        
        // Connect without authentication since the WebSocket API doesn't have an authorizer
        console.log('Connecting to WebSocket without authentication (API has no authorizer)');
        this.socket = new WebSocket(this.webSocketUrl);
        
        this.socket.onopen = () => {
          clearTimeout(connectionTimeout);
          console.log('WebSocket connected successfully');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          
          // Send an initial ping to verify connection
          try {
            this.socket?.send(JSON.stringify({ action: 'ping' }));
          } catch (pingError) {
            console.warn('Error sending initial ping:', pingError);
          }
          
          resolve();
        };
        
        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('WebSocket message received:', data);
            
            // Store connection ID if provided
            if (data.connectionId) {
              this.connectionId = data.connectionId;
              console.log('WebSocket connection ID:', this.connectionId);
            }
            
            // Handle device updates
            if (data.event === 'deviceUpdate') {
              this.messageHandlers.forEach(handler => handler(data));
            }
            
            // Handle pong responses
            if (data.action === 'pong' || data.event === 'pong') {
              console.log('Received pong from server');
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
        
        this.socket.onclose = (event) => {
          clearTimeout(connectionTimeout);
          console.log(`WebSocket disconnected (code: ${event.code}, reason: ${event.reason})`);
          this.isConnected = false;
          
          // Only attempt to reconnect if this wasn't an intentional close
          if (event.code !== 1000) {
            this.handleReconnect();
          }
        };
        
        this.socket.onerror = (error) => {
          clearTimeout(connectionTimeout);
          console.error('WebSocket error:', error);
          this.isConnected = false;
          // Resolve with a warning instead of rejecting
          resolve();
        };
      } catch (error) {
        clearTimeout(connectionTimeout);
        console.error('Error connecting to WebSocket:', error);
        // Resolve with a warning instead of rejecting
        resolve();
      }
    });
  }
  
  /**
   * Connect to WebSocket without authentication as fallback
   */
  private async connectWithoutAuth(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        console.log('Attempting to connect to WebSocket without authentication');
        this.socket = new WebSocket(this.webSocketUrl);
        
        this.socket.onopen = () => {
          console.log('WebSocket connected successfully without auth');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          resolve();
        };
        
        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('WebSocket message received:', data);
            
            if (data.connectionId) {
              this.connectionId = data.connectionId;
            }
            
            if (data.event === 'deviceUpdate') {
              this.messageHandlers.forEach(handler => handler(data));
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
        
        this.socket.onclose = (event) => {
          console.log(`WebSocket disconnected (code: ${event.code}, reason: ${event.reason})`);
          this.isConnected = false;
        };
        
        this.socket.onerror = (error) => {
          console.error('WebSocket error in fallback connection:', error);
          this.isConnected = false;
          reject(error);
        };
      } catch (error) {
        console.error('Error in fallback WebSocket connection:', error);
        reject(error);
      }
    });
  }
  
  /**
   * Disconnect from the WebSocket API
   */
  disconnect(): void {
    if (this.socket && this.isConnected) {
      this.socket.close();
      this.isConnected = false;
    }
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }
  
  /**
   * Subscribe to updates for a specific device
   * @param deviceId The device ID
   */
  async subscribeToDevice(deviceId: string): Promise<void> {
    try {
      if (!this.isConnected) {
        console.log(`Connecting to WebSocket before subscribing to device ${deviceId}`);
        await this.connect();
      }
      
      if (!this.socket || !this.isConnected) {
        throw new Error('WebSocket not connected');
      }
      
      console.log(`Subscribing to device ${deviceId}`);
      this.socket.send(JSON.stringify({
        action: 'subscribe',
        deviceId
      }));
      
      // Add a small delay to ensure the subscription is processed
      await new Promise(resolve => setTimeout(resolve, 100));
      
      return Promise.resolve();
    } catch (error) {
      console.error(`Error subscribing to device ${deviceId}:`, error);
      throw error;
    }
  }
  
  /**
   * Unsubscribe from updates for a specific device
   * @param deviceId The device ID
   */
  async unsubscribeFromDevice(deviceId: string): Promise<void> {
    try {
      if (!this.socket || !this.isConnected) {
        console.log(`Not unsubscribing from device ${deviceId} - WebSocket not connected`);
        return;
      }
      
      console.log(`Unsubscribing from device ${deviceId}`);
      this.socket.send(JSON.stringify({
        action: 'unsubscribe',
        deviceId
      }));
      
      return Promise.resolve();
    } catch (error) {
      console.error(`Error unsubscribing from device ${deviceId}:`, error);
      // Don't throw error on unsubscribe failures
    }
  }
  
  /**
   * Add a handler for device update messages
   * @param handler The message handler function
   */
  addMessageHandler(handler: (message: WebSocketMessage) => void): void {
    this.messageHandlers.push(handler);
  }
  
  /**
   * Remove a message handler
   * @param handler The handler to remove
   */
  removeMessageHandler(handler: (message: WebSocketMessage) => void): void {
    this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
  }
  
  /**
   * Handle reconnection with exponential backoff
   */
  private handleReconnect(): void {
    // Temporarily disable reconnection attempts to avoid error messages
    console.log('WebSocket disconnected - reconnection attempts disabled');
    
    // Log once that we're not reconnecting
    if (this.reconnectAttempts === 0) {
      console.log('WebSocket reconnection is disabled - using HTTP polling instead');
      this.reconnectAttempts = 1; // Just to prevent multiple logs
    }
    
    // Clear any existing timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }
  
  /**
   * Check if the WebSocket is connected
   * @returns True if connected, false otherwise
   */
  isWebSocketConnected(): boolean {
    return this.isConnected;
  }
  
  /**
   * Get the connection ID
   * @returns The connection ID or null if not connected
   */
  getConnectionId(): string | null {
    return this.connectionId;
  }
}

export default new WebSocketService();